import { NODES, type NodeType } from "@repo/fika-blog/src/nodes"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import { NodeSelection } from "@repo/fika-blog/src/tiptap/pm/state"
import type { IconName } from "@repo/fika-design-system/icon"
import { createMemo, Show } from "solid-js"
import { createEditorTransaction } from "solid-tiptap"
import { Mini } from "../components/mini/mini"
import { Tooltip } from "../components/tooltip"

function ParentButton(props: { editor: Editor }) {
  const selection = createMemo(
    createEditorTransaction(
      () => props.editor,
      editor => editor.state.selection,
    ),
  )

  const parentNode = createMemo(() => {
    const sel = selection()
    const { $from } = sel

    if ($from.depth === 0) return null

    let depth = $from.depth
    if ($from.parent.type.name === "text") {
      depth = Math.max(1, depth - 1)
    }

    try {
      const pos = $from.before(depth)
      const node = $from.node(depth)

      if (!node) return null

      return {
        node,
        pos,
        depth,
        name: node.type.name,
      }
    } catch {
      return null
    }
  })

  return (
    <Show
      when={parentNode()}
      fallback={<Mini.Icon name="square-mouse-pointer" disabled />}
    >
      {parent => (
        <Tooltip label={`select parent: ${parent().name}`}>
          <Mini.Button
            iconName="square-mouse-pointer"
            onClick={() => {
              props.editor
                .chain()
                .focus()
                .setParentHighlight(null)
                .command(({ tr }) => {
                  const pos = parent().pos
                  const node = tr.doc.resolve(pos).node()
                  if (!node) return false
                  tr.setSelection(NodeSelection.create(tr.doc, pos))
                  return true
                })
                .run()
            }}
            onMouseEnter={() => {
              props.editor.commands.setParentHighlight(parent().pos)
            }}
            onMouseLeave={() => {
              props.editor.commands.setParentHighlight(null)
            }}
          />
        </Tooltip>
      )}
    </Show>
  )
}

function DeleteButton(props: { editor: Editor }) {
  return (
    <Tooltip label="delete" hue="rose">
      <Mini.Button
        onClick={() => props.editor.chain().focus().deleteSelection().run()}
        iconName="trash-2"
        danger
      />
    </Tooltip>
  )
}

interface TransformTarget {
  iconName: IconName
  label: string
  value: string
}

interface TransformButtonProps {
  editor: Editor
  selection: () => NodeSelection
}

function TransformButton(props: TransformButtonProps) {
  const nodeInfo = createMemo(() => {
    const sel = props.selection()
    const { $from } = sel

    if (sel instanceof NodeSelection) {
      const node = sel.node
      return {
        node,
        pos: sel.from,
        name: node.type.name as NodeType,
      }
    }

    let depth = $from.depth
    if ($from.parent.type.name === "text") {
      depth = Math.max(1, depth - 1)
    }

    const node = $from.node(depth)
    if (!node) throw Error("No node found")

    const pos = depth === 0 ? 0 : $from.before(depth)
    return {
      node,
      pos,
      name: node.type.name as NodeType,
    }
  })
  const selectedValue = createMemo(() => {
    const info = nodeInfo()
    if (info.name === "heading") {
      const level = info.node.attrs.level
      return `heading-${level}`
    }
    return info.name
  })

  return (
    <Tooltip label={selectedValue()}>
      <div
        onMouseEnter={() => {
          const pos = nodeInfo().pos
          if (pos) {
            props.editor.commands.setParentHighlight(pos)
          }
        }}
        onMouseLeave={() => {
          props.editor.commands.setParentHighlight(null)
        }}
      >
        <Mini.Select
          items={getTextWrapperTransformTargets()}
          value={selectedValue()}
          hideValue
          onChange={value => {
            if (!value) return
            const chain = props.editor.chain().focus()
            switch (value) {
              case "heading-1":
                return chain.setNode("heading", { level: 1 }).run()
              case "heading-2":
                return chain.setNode("heading", { level: 2 }).run()
              case "heading-3":
                return chain.setNode("heading", { level: 3 }).run()
              case "paragraph":
                return chain.setParagraph().run()
              case "codeBlock":
                return chain.setNode("codeBlock").run()
              default:
                return false
            }
          }}
        />
      </div>
    </Tooltip>
  )
}

function getTextWrapperTransformTargets(): TransformTarget[] {
  return [
    {
      value: "paragraph",
      label: NODES.paragraph.label,
      iconName: NODES.paragraph.iconName,
    },
    { value: "heading-1", label: "heading 1", iconName: "heading-1" },
    { value: "heading-2", label: "heading 2", iconName: "heading-2" },
    { value: "heading-3", label: "heading 3", iconName: "heading-3" },
    {
      value: "codeBlock",
      label: NODES.codeBlock.label,
      iconName: NODES.codeBlock.iconName,
    },
  ]
}

export { ParentButton, DeleteButton, TransformButton }
